import { createContext, useContext, useState, type ReactNode } from "react";

export type IntentStage = "previous" | "upcoming" | "midseason";
export type IntentTrack = "profit" | "seats" | "brand";

export interface IntentState {
  stage: IntentStage | null;
  track: IntentTrack | null;
  firstName: string;
  email: string;
  promoCode: string;
}

interface IntentContextType {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  state: IntentState;
  setState: (updater: (prev: IntentState) => IntentState) => void;
  reset: () => void;
}

const initialState: IntentState = {
  stage: null,
  track: null,
  firstName: "",
  email: "",
  promoCode: "NEWBIE",
};

const IntentContext = createContext<IntentContextType | null>(null);

export function IntentProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<IntentState>(initialState);

  const open = () => setIsOpen(true);
  const close = () => {
    setIsOpen(false);
    // Reset state when modal closes (per brief)
    setTimeout(() => setState(initialState), 300);
  };
  const reset = () => setState(initialState);

  return (
    <IntentContext.Provider value={{ isOpen, open, close, state, setState, reset }}>
      {children}
    </IntentContext.Provider>
  );
}

export function useIntent() {
  const ctx = useContext(IntentContext);
  if (!ctx) throw new Error("useIntent must be used within IntentProvider");
  return ctx;
}

// Stage configuration (colors mapped to existing dashboard palette)
export const STAGE_CONFIG = {
  previous: {
    icon: "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z",
    label: "Analyse my previous show(s)",
    sub: "Season's over — show me what happened",
    detail: "Your data is sitting there doing nothing. We'll turn it into a plan for next time.",
    color: "#eaac55",
    colorRgb: "234, 172, 85",
    tag: "Past Season Review",
  },
  upcoming: {
    icon: "M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75",
    label: "I have a show coming up",
    sub: "Registered for an upcoming festival season",
    detail: "Get set up now so your live dashboard activates the moment tickets go on sale.",
    color: "#32caab",
    colorRgb: "50, 202, 171",
    tag: "Upcoming Season",
  },
  midseason: {
    icon: "M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z",
    label: "I'm mid-season right now",
    sub: "Currently performing at a festival",
    detail: "Connect now and your live dashboard starts tracking immediately. Every ticket, every show, right now.",
    color: "#cb505f",
    colorRgb: "203, 80, 95",
    tag: "Mid-Season",
  },
} as const;

export const TRACK_COPY = {
  profit: {
    icon: "\uD83D\uDCB0",
    label: "Maximise Profit",
    descriptions: {
      previous: "See where money leaked. Get a pricing and cost strategy for next season.",
      upcoming: "Optimise every dollar. Pricing strategy, cost tracking, revenue-focused missions.",
      midseason: "See exactly where you stand. Daily missions to push revenue before closing night.",
    },
  },
  seats: {
    icon: "\uD83C\uDFDF\uFE0F",
    label: "Fill Every Seat",
    descriptions: {
      previous: "Find the attendance patterns. Know exactly when and why seats went empty.",
      upcoming: "Bums in seats. Volume tactics, group offers, attendance-focused coaching.",
      midseason: "Real-time attendance tracking. Urgent tactics to fill remaining shows.",
    },
  },
  brand: {
    icon: "\u2B50",
    label: "Build My Brand",
    descriptions: {
      previous: "Audit your reviews and reach. Build a reputation roadmap for next time.",
      upcoming: "Reviews, press, reputation. Play the long game and build a name.",
      midseason: "Capture reviews and press while the buzz is hot. Track every mention.",
    },
  },
} as const;

export const STAGE_HEADLINES = {
  previous: { title: "YOUR DATA HAS STORIES TO TELL.", sub: "What do you want to optimise next time?" },
  upcoming: { title: "SMART MOVE \u2014 SETTING UP EARLY.", sub: "What matters most for this season?" },
  midseason: { title: "LET'S GET YOU RESULTS RIGHT NOW.", sub: "What are you chasing this season?" },
} as const;

export const SIGNUP_HEADLINES = {
  previous: { title: "LET'S BUILD YOUR SEASON REVIEW", button: "BUILD MY SEASON REVIEW" },
  upcoming: { title: "LET'S GET YOU LAUNCH-READY", button: "SET UP MY SEASON" },
  midseason: { title: "LET'S GET YOUR DASHBOARD LIVE", button: "LAUNCH MY DASHBOARD" },
} as const;

export const SUCCESS_COPY = {
  previous: {
    body: "Your Season Review is about to come to life. Here's what happens now:",
    step3: "Your Season Review builds automatically \u2014 every show, every ticket, every insight",
    cta: "CONNECT MY DATA",
  },
  upcoming: {
    body: "You're ahead of the game. Here's what happens now:",
    step3: "Your pre-season planner and readiness score are built \u2014 daily missions start rolling in",
    cta: "GO TO MY DASHBOARD",
  },
  midseason: {
    body: "Your live dashboard is about to go live. Here's what happens now:",
    step3: "Your live dashboard activates immediately \u2014 real-time sales, daily missions, smart alerts",
    cta: "GO TO MY DASHBOARD",
  },
} as const;
